import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Button, Card, TextInput } from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { List as TaskTypesList } from '../parameters/task_type';
import { List as TaskCategoriesList } from '../parameters/task_category';
import { List as TaskStatusList } from '../parameters/task_status';
import { List as PrioritiesList } from '../parameters/priority';
import { List as TicketStatusList } from '../parameters/ticket_status';
import { List as CallTypesList } from '../parameters/call_type';
import { List as ServiceTypesList } from '../parameters/service_type';
import { List as FrequencyTypesList } from '../parameters/frequency_type';
import { List as DurationsList } from '../parameters/duration';
import { List as ServiceCategoriesList } from '../parameters/p_crm_service_category';
import { List as ReminderTypesList } from '../parameters/reminder_type';
import { List as TaxAuthoritiesList } from '../parameters/tax_authority';
import { List as CountriesList } from '../parameters/p_country';
import { List as AppStatusList } from '../parameters/app_status';
import { List as InvoiceCategoriesList } from '../parameters/p_crm_invoice_category';
import { List as InvoiceTypesList } from '../parameters/p_crm_invoice_type';
import { List as ManufacturersList } from '../parameters/manufacturer';
import { List as CatalogCategoriesList } from '../parameters/catalog-category';
import { List as TagsList } from '../parameters/tags';
import { List as ExpenseCategoriesManagerList } from '../expense-categories-manager';
import AiModelsSettings from './AiModelsSettings';
import ApplicationSettingsModule from './ApplicationSettingsModule';
import FilesStoragePage from '../settings/FilesStoragePage';
import SystemEmailTemplatesModule from './SystemEmailTemplatesModule';
import EmailNotificationSettingsModule from './EmailNotificationSettingsModule';

export const moduleConfig = {
  module: {
    title: 'Configuration & Settings',
    description: 'Manage parameter modules from a central hub.',
  },
};

type ConfigGroupId =
  | 'system'
  | 'sales_marketing'
  | 'tasks_support'
  | 'projects_services'
  | 'billing_payments'
  | 'expenses_management'
  | 'offers_catalog'
  | 'ai_automation';

interface ConfigModuleDefinition {
  id: string;
  title: string;
  description: string;
  route: string;
  permissionModuleKey: string;
  /** If true, only ADMIN users see this module in the hub (still gated by permissionModuleKey when not admin). */
  adminOnly?: boolean;
  /**
   * When true, the hub renders this module with `embedded` so the page can skip duplicate chrome
   * (`EmbeddedInConfigurationHub` in `./models`).
   */
  hubPassesEmbedded?: boolean;
  component: React.ComponentType;
}

interface ConfigGroupDefinition {
  id: ConfigGroupId;
  title: string;
  description: string;
  modules: ConfigModuleDefinition[];
}

const CONFIG_GROUPS: ConfigGroupDefinition[] = [
  {
    id: 'system',
    title: 'System',
    description: 'Application-wide settings visible to administrators.',
    modules: [
      {
        id: 'application_settings',
        title: 'Application settings',
        description:
          'Feature flags, API timeouts, dashboard layout, and company profile. SMTP lives under Email notifications.',
        route: '/profile-settings?tab=application',
        permissionModuleKey: 'configuration_settings',
        adminOnly: true,
        hubPassesEmbedded: true,
        component: ApplicationSettingsModule,
      },
      {
        id: 'email_notifications',
        title: 'Email notifications',
        description: 'Enable transactional email, configure default SMTP, and send a test message.',
        route: '/admin/settings/email-notifications',
        permissionModuleKey: 'configuration_settings',
        adminOnly: true,
        hubPassesEmbedded: true,
        component: EmailNotificationSettingsModule,
      },
      {
        id: 'system_email_templates',
        title: 'System email templates',
        description: 'Customize subject and HTML for invitations, password reset, task notifications, and signatures.',
        route: '/admin/settings/system-email-templates',
        permissionModuleKey: 'configuration_settings',
        adminOnly: true,
        hubPassesEmbedded: true,
        component: SystemEmailTemplatesModule,
      },
      {
        id: 'files_storage',
        title: 'File storage',
        description: 'Audit file attachments on disk, review orphaned records, and manage soft-deleted files.',
        route: '/admin/settings/files-storage',
        permissionModuleKey: 'deleted_files',
        component: FilesStoragePage,
      },
    ],
  },
  {
    id: 'ai_automation',
    title: 'AI & Automation',
    description: 'Manage AI models and provider metadata.',
    modules: [
      {
        id: 'ai_models',
        title: 'AI Models',
        description: 'Save model/provider configuration used by AI features.',
        route: '/configuration-settings?group=ai_automation&module=ai_models',
        permissionModuleKey: 'configuration_settings',
        hubPassesEmbedded: true,
        component: AiModelsSettings,
      },
    ],
  },
  {
    id: 'sales_marketing',
    title: 'Clients & CRM',
    description: 'Statuses and parameter modules used by leads, calls, reminders, and clients.',
    modules: [
      {
        id: 'call_type',
        title: 'Call Type',
        description: 'Maintain the available call type values.',
        route: '/call_type',
        permissionModuleKey: 'call_type',
        component: CallTypesList,
      },
      {
        id: 'reminder_type',
        title: 'Reminder Types',
        description: 'Maintain reminder type options.',
        route: '/reminder_type',
        permissionModuleKey: 'reminder_type',
        component: ReminderTypesList,
      },
      {
        id: 'app_status',
        title: 'Appointment & Lead Status',
        description: 'Maintain the shared status options and badge colors for appointments and leads.',
        route: '/app_status',
        permissionModuleKey: 'app_status',
        component: AppStatusList,
      },
      {
        id: 'p_country',
        title: 'Countries',
        description: 'Maintain the list of countries.',
        route: '/p_country',
        permissionModuleKey: 'p_country',
        component: CountriesList,
      },
    ],
  },
  {
    id: 'tasks_support',
    title: 'Tasks & Support',
    description: 'Parameters used by CRM tasks and support tickets.',
    modules: [
      {
        id: 'task_type',
        title: 'Task Types',
        description: 'Maintain the task type options used in tasks.',
        route: '/task_type',
        permissionModuleKey: 'task_type',
        component: TaskTypesList,
      },
      {
        id: 'task_category',
        title: 'Task Categories',
        description: 'Maintain task categories used for CRM tasks.',
        route: '/task_category',
        permissionModuleKey: 'task_category',
        component: TaskCategoriesList,
      },
      {
        id: 'task_status',
        title: 'Task Status',
        description: 'Maintain task statuses and badge colors.',
        route: '/task_status',
        permissionModuleKey: 'task_status',
        component: TaskStatusList,
      },
      {
        id: 'priority',
        title: 'Priorities',
        description: 'Maintain priority options used across support and tasks.',
        route: '/priority',
        permissionModuleKey: 'priority',
        component: PrioritiesList,
      },
      {
        id: 'ticket_status',
        title: 'Ticket Status',
        description: 'Maintain support ticket statuses and badge colors.',
        route: '/ticket_status',
        permissionModuleKey: 'ticket_status',
        component: TicketStatusList,
      },
    ],
  },
  {
    id: 'projects_services',
    title: 'Projects & Services',
    description: 'Service-related parameter modules used by projects and recurring services.',
    modules: [
      {
        id: 'service_type',
        title: 'Service Types',
        description: 'Maintain service type options.',
        route: '/service_type',
        permissionModuleKey: 'service_type',
        component: ServiceTypesList,
      },
      {
        id: 'frequency_type',
        title: 'Frequency Types',
        description: 'Maintain recurring frequency options.',
        route: '/frequency_type',
        permissionModuleKey: 'frequency_type',
        component: FrequencyTypesList,
      },
      {
        id: 'duration',
        title: 'Durations',
        description: 'Maintain duration presets used by appointments and services.',
        route: '/duration',
        permissionModuleKey: 'duration',
        component: DurationsList,
      },
      {
        id: 'p_crm_service_category',
        title: 'Service Categories',
        description: 'Maintain service category values.',
        route: '/p_crm_service_category',
        permissionModuleKey: 'service_category',
        component: ServiceCategoriesList,
      },
    ],
  },
  {
    id: 'billing_payments',
    title: 'Billing & Payments',
    description: 'Financial parameter modules used by invoicing and tax-related flows.',
    modules: [
      {
        id: 'tax_authority',
        title: 'Tax Authorities',
        description: 'Maintain tax authority values.',
        route: '/tax_authority',
        permissionModuleKey: 'tax_authority',
        component: TaxAuthoritiesList,
      },
      {
        id: 'p_crm_invoice_category',
        title: 'Invoice Categories',
        description: 'Maintain invoice category values.',
        route: '/p_crm_invoice_category',
        permissionModuleKey: 'invoice_category',
        component: InvoiceCategoriesList,
      },
      {
        id: 'p_crm_invoice_type',
        title: 'Invoice Types',
        description: 'Maintain invoice type values.',
        route: '/p_crm_invoice_type',
        permissionModuleKey: 'invoice_type',
        component: InvoiceTypesList,
      },
    ],
  },
  {
    id: 'offers_catalog',
    title: 'Offers & Catalog',
    description: 'Catalog-related parameter modules used by offers and product data.',
    modules: [
      {
        id: 'manufacturer',
        title: 'Manufacturers',
        description: 'Maintain manufacturer options for catalog items.',
        route: '/manufacturer',
        permissionModuleKey: 'manufacturer',
        component: ManufacturersList,
      },
      {
        id: 'catalog-category',
        title: 'Catalog Categories',
        description: 'Maintain category values for catalog items.',
        route: '/catalog-category',
        permissionModuleKey: 'catalog_category',
        component: CatalogCategoriesList,
      },
      {
        id: 'tags',
        title: 'Tags',
        description: 'Maintain shared tagging options.',
        route: '/tags',
        permissionModuleKey: 'tags',
        component: TagsList,
      },
    ],
  },
  {
    id: 'expenses_management',
    title: 'Payables',
    description: 'Expense category parameters used by payables, payees, and recurring payables.',
    modules: [
      {
        id: 'expense_categories',
        title: 'Expense Categories & Sub-categories',
        description: 'Manage expense categories and subcategories in one combined view.',
        route: '/expense-categories',
        permissionModuleKey: 'expense_categories',
        component: ExpenseCategoriesManagerList,
      },
    ],
  },
];

const ConfigurationSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { colors } = useTheme();
  const { user, can, permissionsLoaded } = useAuth();

  const searchTerm = searchParams.get('search') || '';
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  const canView = React.useCallback(
    (moduleKey: string) => {
      if (!permissionsLoaded) return true;
      return can(moduleKey, 'view');
    },
    [can, permissionsLoaded]
  );

  const visibleGroups = React.useMemo(() => {
    return CONFIG_GROUPS.map((group) => ({
      ...group,
      modules: group.modules.filter((module) => {
        if (module.adminOnly && user?.role !== 'ADMIN') {
          return false;
        }
        return canView(module.permissionModuleKey);
      }),
    })).filter((group) => group.modules.length > 0);
  }, [canView, user?.role]);

  const selectedGroupIdFromUrl = searchParams.get('group');
  const selectedGroup =
    visibleGroups.find((group) => group.id === selectedGroupIdFromUrl) ?? visibleGroups[0];

  const selectedModuleIdFromUrl = searchParams.get('module');
  const selectedModule =
    selectedGroup?.modules.find((module) => module.id === selectedModuleIdFromUrl) ??
    selectedGroup?.modules[0];

  const filteredModules = React.useMemo(() => {
    if (!selectedGroup) {
      return [];
    }
    if (!normalizedSearchTerm) {
      return selectedGroup.modules;
    }
    return selectedGroup.modules.filter((module) => {
      const haystack = `${module.title} ${module.description}`.toLowerCase();
      return haystack.includes(normalizedSearchTerm);
    });
  }, [normalizedSearchTerm, selectedGroup]);

  const displayedSelectedModule = React.useMemo(() => {
    if (!selectedModule) {
      return undefined;
    }
    if (!normalizedSearchTerm) {
      return selectedModule;
    }
    return filteredModules.find((module) => module.id === selectedModule.id) ?? filteredModules[0];
  }, [filteredModules, normalizedSearchTerm, selectedModule]);

  const updateParams = React.useCallback(
    (next: { group?: string; module?: string; search?: string }) => {
      const params = new URLSearchParams(searchParams);
      if (next.group !== undefined) {
        if (next.group) {
          params.set('group', next.group);
        } else {
          params.delete('group');
        }
      }
      if (next.module !== undefined) {
        if (next.module) {
          params.set('module', next.module);
        } else {
          params.delete('module');
        }
      }
      if (next.search !== undefined) {
        if (next.search.trim()) {
          params.set('search', next.search);
        } else {
          params.delete('search');
        }
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  React.useEffect(() => {
    if (!selectedGroup || !selectedModule) {
      return;
    }

    if (selectedGroup.id !== selectedGroupIdFromUrl || selectedModule.id !== selectedModuleIdFromUrl) {
      updateParams({
        group: selectedGroup.id,
        module: selectedModule.id,
      });
    }
  }, [
    selectedGroup,
    selectedModule,
    selectedGroupIdFromUrl,
    selectedModuleIdFromUrl,
    updateParams,
  ]);

  if (visibleGroups.length === 0) {
    return (
      <div className={cn('flex flex-col flex-1 min-w-0 min-h-0', colors.background)}>
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden dashboard-scrollbar">
          <div className="p-6 sm:p-8 max-w-6xl mx-auto w-full">
            <Card className="p-8 text-center">
              <h1 className={cn('text-2xl font-semibold', colors.text)}>Configuration & Settings</h1>
              <p className={cn('mt-3 text-sm', colors.textMuted)}>
                No configuration modules are available for your current permissions.
              </p>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const SelectedListComponent = displayedSelectedModule?.component;

  return (
    <div className={cn('flex flex-col flex-1 min-w-0 min-h-0', colors.background)}>
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden dashboard-scrollbar">
        <div className="p-6 sm:p-8 max-w-7xl mx-auto w-full space-y-6">
          <section className="space-y-2">
            <div className={cn('text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>
              Groups
            </div>
            <div className="flex flex-wrap gap-2">
              {visibleGroups.map((group) => {
                const isSelected = group.id === selectedGroup?.id;
                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() =>
                      updateParams({
                        group: group.id,
                        module: group.modules[0]?.id || '',
                      })
                    }
                    className={cn(
                      'inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-all hover:shadow-sm',
                      colors.surface,
                      isSelected ? colors.primaryBorderAll : colors.border,
                      isSelected ? colors.text : colors.textSecondary
                    )}
                  >
                    <span className="truncate">{group.title}</span>
                    <Badge
                      variant="secondary"
                      className={cn(
                        'rounded-md px-1.5 py-0 text-[11px]',
                        isSelected
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700/60 dark:text-gray-300'
                      )}
                    >
                      {group.modules.length}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </section>

          {selectedGroup && (
            <section className="space-y-3">
              <Card className="p-3">
                <div className="flex flex-col gap-3">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <h2 className={cn('text-base font-semibold', colors.text)}>{selectedGroup.title}</h2>
                      <p className={cn('text-xs', colors.textMuted)}>{selectedGroup.description}</p>
                    </div>
                    <div className="w-full lg:max-w-xs">
                      <TextInput
                        value={searchTerm}
                        onChange={(event) => updateParams({ search: event.target.value })}
                        placeholder={`Search ${selectedGroup.title.toLowerCase()}...`}
                      />
                    </div>
                  </div>

                  <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                    {filteredModules.map((module) => {
                      const isSelected = module.id === displayedSelectedModule?.id;
                      return (
                        <button
                          key={module.id}
                          type="button"
                          onClick={() => updateParams({ module: module.id })}
                          className={cn(
                            'text-left rounded-lg border px-3 py-2 transition-all hover:shadow-sm',
                            colors.surface,
                            isSelected ? colors.primaryBorderAll : colors.border
                          )}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className={cn('truncate text-sm font-semibold', colors.text)}>
                              {module.title}
                            </div>
                            {isSelected && (
                              <Badge
                                variant="secondary"
                                className="rounded-md bg-indigo-100 px-1.5 py-0 text-[11px] text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300"
                              >
                                Open
                              </Badge>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </Card>

              {filteredModules.length === 0 && (
                <Card className="p-6 text-center">
                  <p className={cn('text-sm', colors.textMuted)}>
                    No modules in {selectedGroup.title} match your search.
                  </p>
                </Card>
              )}
            </section>
          )}

          {displayedSelectedModule && SelectedListComponent && (
            <section className="space-y-4">
              <Card className="p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-1">
                    <div className={cn('text-lg font-semibold', colors.text)}>
                      {displayedSelectedModule.title}
                    </div>
                    <p className={cn('text-xs', colors.textMuted)}>
                      {displayedSelectedModule.description}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(displayedSelectedModule.route)}
                  >
                    Open standalone page
                  </Button>
                </div>
              </Card>

              {displayedSelectedModule.hubPassesEmbedded
                ? React.createElement(SelectedListComponent as React.ComponentType<{ embedded?: boolean }>, {
                    embedded: true,
                  })
                : React.createElement(SelectedListComponent)}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurationSettingsPage;
